import { Loader2, Sparkles, TrendingDown } from 'lucide-react'
import type { AiCheckSummary, AiVerdict, LateRisk } from '@/types'
import Badge, { type BadgeTone } from '@/components/ui/Badge'
import { cn } from '@/lib/cn'

const VERDICT: Record<AiVerdict, { label: string; tone: BadgeTone }> = {
  on_track: { label: 'On track', tone: 'success' },
  at_risk: { label: 'At risk', tone: 'warning' },
  late: { label: 'Late', tone: 'danger' },
}

/** An MPG AI check's verdict, or its state while it is being written. */
export function VerdictBadge({ check, className }: { check?: Pick<AiCheckSummary, 'status' | 'verdict'> | null; className?: string }) {
  if (!check) return null

  if (check.status === 'queued' || check.status === 'running') {
    return (
      <Badge tone="neutral" className={cn('whitespace-nowrap', className)}>
        <Loader2 className="size-3 animate-spin" /> Checking
      </Badge>
    )
  }

  if (check.status === 'failed') {
    return (
      <Badge tone="neutral" className={cn('whitespace-nowrap', className)}>
        Check failed
      </Badge>
    )
  }

  if (!check.verdict) return null
  const { label, tone } = VERDICT[check.verdict]

  return (
    <Badge tone={tone} className={cn('whitespace-nowrap', className)}>
      <Sparkles className="size-3" /> {label}
    </Badge>
  )
}

const RISK: Record<LateRisk['level'], { label: string; tone: BadgeTone }> = {
  overdue: { label: 'Overdue', tone: 'danger' },
  likely_late: { label: 'Likely late', tone: 'danger' },
  tight: { label: 'Tight', tone: 'warning' },
}

/**
 * The late-risk flag: will this job make its delivery date at the pace the
 * sections have really been working? The reason is the hover text.
 */
export function RiskBadge({
  risk,
  size,
  className,
}: {
  risk?: LateRisk | null
  size?: 'sm' | 'md'
  className?: string
}) {
  if (!risk) return null
  const { label, tone } = RISK[risk.level]

  return (
    <span title={risk.reason} className={cn('inline-flex', className)}>
      <Badge tone={tone} size={size} className="whitespace-nowrap">
        <TrendingDown className="size-3" /> {label}
      </Badge>
    </span>
  )
}
